import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Analysis } from '../../types/analysis';
import { InfoTooltip } from '../common/InfoTooltip';

interface CommunicationsTableProps {
  analyses: Analysis[];
  analysisLinkPrefix?: string; // e.g. '/admin/analysis' for admin views
}

export const CommunicationsTable: React.FC<CommunicationsTableProps> = ({ analyses, analysisLinkPrefix = '/analysis' }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Calculate pagination
  const totalPages = Math.ceil(analyses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAnalyses = analyses.slice(startIndex, endIndex);

  // Build a compact list of pagination items (numbers + ellipses)
  const getPaginationItems = () => {
    const items: (number | 'ellipsis')[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(i);
      }
      return items;
    }

    const windowSize = 1; // neighbours around current page
    const start = Math.max(2, currentPage - windowSize);
    const end = Math.min(totalPages - 1, currentPage + windowSize);

    items.push(1);

    if (start > 2) {
      items.push('ellipsis');
    }

    for (let i = start; i <= end; i++) {
      items.push(i);
    }

    if (end < totalPages - 1) {
      items.push('ellipsis');
    }

    items.push(totalPages);

    return items;
  };

  // Get sentiment label and color
  const getSentimentInfo = (analysis: Analysis) => {
    const sentiment = analysis.sentiment;
    if (!sentiment) return { label: 'Unknown', color: 'bg-gray-100 text-gray-800' };

    // Use the raw sentiment values from API
    const positivity = (sentiment as any).positivity || 0;
    const neutral = (sentiment as any).neutral || 0;
    const negativity = (sentiment as any).negativity || 0;
    
    if (positivity >= neutral && positivity >= negativity) {
      return { label: 'Positive', color: 'bg-green-100 text-green-800' };
    } else if (negativity >= neutral && negativity >= positivity) {
      return { label: 'Negative', color: 'bg-red-100 text-red-800' };
    } else {
      return { label: 'Neutral', color: 'bg-blue-100 text-blue-800' };
    }
  };

  // Get clarity score
  const getClarityScore = (analysis: Analysis) => {
    // Check if clarity_analysis exists and has calculated_clarity_score
    const clarityScore = analysis.clarity_analysis?.calculated_clarity_score;
    return clarityScore !== undefined ? clarityScore : null;
  };

  // Get trait strength color
  const getTraitColor = (score: number) => {
    if (score >= 0.7) return 'bg-green-100 text-green-800';
    if (score >= 0.4) return 'bg-blue-100 text-blue-800';
    return 'bg-orange-100 text-orange-800';
  };

  // Get trait label
  const getTraitLabel = (score: number) => {
    if (score >= 0.7) return 'Strong';
    if (score >= 0.4) return 'Neutral';
    return 'Weak';
  };

  // Get top 3 traits
  const getTopTraits = (analysis: Analysis) => {
    if (!analysis.trait_analysis) return [];
    
    const traits = Object.entries(analysis.trait_analysis)
      .filter(([key]) => key.endsWith('_score'))
      .map(([key, score]) => ({ 
        name: key.replace('_score', '').replace(/_/g, ' '),
        score: score as number 
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
    
    return traits;
  };

  // Get communication title/subject
  const getCommunicationTitle = (analysis: any) => {
    // Try direct title field first
    if (analysis.title) {
      return analysis.title;
    }
    // Try metadata subject
    if (analysis.metadata?.subject) {
      return analysis.metadata.subject;
    }
    // Fall back to truncated text
    if (analysis.text) {
      return truncateText(analysis.text, 60);
    }
    return 'Untitled Communication';
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Truncate text
  const truncateText = (text: string, maxLength: number = 60) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Communication History</h2>
        <p className="text-sm text-gray-600 mt-1">
          Browse and review all your analyzed communications
        </p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Date</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Communication</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Sentiment</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Clarity</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">
                <div className="flex items-center gap-2">
                  Top Traits
                  <InfoTooltip
                    title="Trait Color Coding"
                    content={[
                      "Traits are color-coded by strength:",
                      "🟢 Green: Strong (≥70%) - Highly present in communication",
                      "🔵 Blue: Neutral (40-69%) - Moderately present",
                      "🟠 Orange: Weak (<40%) - Minimally present",
                      "Shows the top 3 most prominent traits from each analysis."
                    ]}
                    position="left"
                  />
                </div>
              </th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">Action</th>
            </tr>
          </thead>
          <tbody>
            {currentAnalyses.map((analysis) => {
              const sentimentInfo = getSentimentInfo(analysis);
              const clarityScore = getClarityScore(analysis);
              const topTraits = getTopTraits(analysis);

              return (
                <tr 
                  key={analysis._id} 
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  {/* Date */}
                  <td className="py-4 px-4 text-sm text-gray-600">
                    {formatDate(analysis.date)}
                  </td>

                  {/* Communication Title/Subject */}
                  <td className="py-4 px-4">
                    <div className="max-w-xs">
                      <p className="text-sm text-gray-900">
                        {getCommunicationTitle(analysis)}
                      </p>
                    </div>
                  </td>

                  {/* Sentiment */}
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sentimentInfo.color}`}>
                      {sentimentInfo.label}
                    </span>
                  </td>

                  {/* Clarity Score */}
                  <td className="py-4 px-4">
                    {clarityScore !== null ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {clarityScore.toFixed(1)}/10
                        </span>
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-indigo-600 h-2 rounded-full transition-all"
                            style={{ width: `${(clarityScore / 10) * 100}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">N/A</span>
                    )}
                  </td>

                  {/* Top Traits */}
                  <td className="py-4 px-4">
                    <div className="flex flex-wrap gap-1">
                      {topTraits.map((trait, index) => (
                        <span
                          key={index}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTraitColor(trait.score)}`}
                          title={`${trait.name}: ${Math.round(trait.score * 100)}% (${getTraitLabel(trait.score)})`}
                        >
                          {trait.name}
                        </span>
                      ))}
                    </div>
                  </td>

                  {/* Action */}
                  <td className="py-4 px-4 text-right">
                    <button
                      onClick={() => navigate(`${analysisLinkPrefix}/${(analysis as any).id || analysis._id}`)}
                      className="text-indigo-600 hover:text-indigo-700 text-sm font-medium transition-colors"
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-gray-600">
            Showing {startIndex + 1}-{Math.min(endIndex, analyses.length)} of {analyses.length} communications
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            
            <div className="flex flex-wrap items-center gap-1">
              {getPaginationItems().map((item, index) => {
                if (item === 'ellipsis') {
                  return (
                    <span key={`ellipsis-${index}`} className="px-2 text-sm text-gray-400">
                      …
                    </span>
                  );
                }

                const pageNumber = item as number;
                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNumber
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
